import React, { useState, useEffect, useRef, useCallback } from "react";
import { Alert, Typography, Tooltip, IconButton } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import {
    Delete as DeleteIcon,
    MyLocation as MyLocationIcon,
    LocationOff as LocationOffIcon,
    SelectAll as SelectAllIcon,
    Deselect as DeselectIcon
} from "@mui/icons-material";

//components
import ConfirmationDialog from "../../../components/Dialog/ConfirmationDialog";
import Legend from "../../../components/Legend/Legend";
import Texas100RoadSegmentsMap from "../../../components/Map/Texas100RoadSegmentsMap";

//formik
import { useFormikContext } from 'formik';

//custom hook
import { useFetch } from "../../../hooks/useFetch";

//constants
import { API_URLS, SEGMENT_COLORS } from "../../../Constants";

//context
import { useAuth } from "../../../context/AuthContext";

//themes
import themes from "../../../themes/defaultTheme";

export default function ConnectedRoadways(props) {
    const { auth } = useAuth();
    const loggedInUserRole = auth?.user?.roleName || null;
    const [connectedRoadwaysData, setConnectedRoadwaysData] = useState([]);
    const fetcher = useFetch("get", API_URLS["GET_TEXAS100_MONITORED_ROADWAYS"]);
    const [addedSegmentIds, setAddedSegmentIds] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [highlightedIds, setHighlightedIds] = useState([]);
    const [lastHighlightedId, setLastHighlightedId] = useState(null);
    const [announcement, setAnnouncement] = useState("");
    const [editingSegmentId, setEditingSegmentId] = useState(null);
    const editingSegmentIdRef = useRef(null);
    const layerRefs = useRef({});
    const [confirmationDialog, setConfirmationDialog] = useState({
        open: false,
        title: "",
        message: "",
        onConfirm: () => { }, // Default empty function
    });
    const { values } = props;
    const formik = useFormikContext();

    //Fetch data from API when the component renders.
    useEffect(() => {

        //If loggedin user is super admin or TTI user, fetch all monitored roads
        if (loggedInUserRole.toLowerCase() === "super admin" || loggedInUserRole.toLowerCase() === "tti user")
            fetcher.setRequest({});
        else if (auth?.user?.regionAccessibleSet) {
            const userRegionsIds = auth.user.regionAccessibleSet.map(region => region.id);
            const projImpactedRegionIds = values?.impactedRegions?.length > 0
                ? values.impactedRegions.filter(reg => reg.id !== null)?.map(reg => reg.id)
                : [];
            const resRegions = [...userRegionsIds, ...projImpactedRegionIds]?.join(",");
            if (userRegionsIds?.length === 1) {
                const urlWithRegionIds = `${API_URLS["GET_TEXAS100_MONITORED_ROADWAYS"]}?district=${resRegions}`;
                fetcher.setRequest({ passedUrl: urlWithRegionIds });
            }
        }

    }, [loggedInUserRole]);

    //Once response is received, set appropriate state variables.
    useEffect(() => {
        const response = fetcher.serverResponse;
        if (response?.length > 0) {
            setConnectedRoadwaysData(response || []);
        }
    }, [fetcher.serverResponse]);

    //updates table data when a new roadway is selected in edit/review page 
    useEffect(() => {

        if (values.connectedRoadways.length > 0) {

            setTableData(values.connectedRoadways);

            //Add previously selected segment Ids for geoJSONStyle to mark them green.

            setAddedSegmentIds(Array.from(new Set(addedSegmentIds.concat(values.connectedRoadways.map((i) => {
                return parseInt(i.segmentId)

            })))));
        }
    }, [values.connectedRoadways]);

    //Sync editingSegmentIdRef with state
    useEffect(() => {
        editingSegmentIdRef.current = editingSegmentId;
    }, [editingSegmentId]);

    //Wires DOM event listeners inside a popup after Leaflet has inserted the content.
    function wirePopupEvents(layer, feature) {
        const popupEl = layer.getPopup().getElement();
        const radioEls = popupEl?.querySelectorAll('[data-impact-select]');
        const btnWrapper = popupEl?.querySelector('[data-btn-wrapper]');
        const errorEl = popupEl?.querySelector('[data-error]');

        radioEls?.forEach(radio => {
            radio.addEventListener('change', () => {
                if (btnWrapper) btnWrapper.style.display = 'flex';
                if (errorEl) errorEl.style.display = 'none';
            });
        });

        popupEl?.querySelector('[data-add-btn]')?.addEventListener('click', () => {
            const impactAreaVal = popupEl?.querySelector('[data-impact-select]:checked')?.value || 'None';
            if (impactAreaVal !== 'None') {
                handleAddSegment(feature, impactAreaVal, layer);
            }
        });
    }

    //Wires DOM event listeners for the edit mode popup
    function wireEditPopupEvents(layer, feature) {
        const popupEl = layer.getPopup().getElement();
        const deleteBtn = popupEl?.querySelector('[data-delete-edit-btn]');
        const updateBtn = popupEl?.querySelector('[data-update-btn]');

        // Handle Save button click
        updateBtn?.addEventListener('click', () => {
            const impactAreaVal = popupEl?.querySelector('[data-impact-select-edit]:checked')?.value || 'None';
            if (impactAreaVal !== 'None') {
                handleUpdateSegmentFromPopup(feature, impactAreaVal, layer);
            }
        });

        // Handle Delete button click - use handleDeleteSelected for confirmation dialog
        deleteBtn?.addEventListener('click', () => {
            handleDeleteSelected([feature.id], feature.properties.road);
            layer.closePopup();
        });
    }

    //Renders different events for each road segment — HTML popup with impact-area selector.
    const visuallyHidden = "position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0;";

    function onEachRoad(feature, layer) {
        const fontFamily = themes.default.typography.fontFamily;
        const errorColor = themes.default.palette.error.dark;
        const successColor = themes.default.palette.success.dark;
        const primaryColor = themes.default.palette.primary.main;
        const primaryContrast = themes.default.palette.primary.contrastText;

        layer.bindPopup('', { maxWidth: 520, minWidth: 380 });
        layerRefs.current[feature.id] = layer;

        layer.on('click', () => {
            setHighlightedIds([feature.id]);
            setLastHighlightedId(feature.id);
            setAnnouncement(`Segment ${feature.properties.road} from ${feature.properties.from_road} to ${feature.properties.to_road} highlighted.`);
        });

        layer.on('popupopen', () => {
            const isAdded = addedSegmentIds.includes(feature.id);
            const isEditing = editingSegmentIdRef.current === feature.id;
            const { road, from_road, to_road, arank } = feature.properties;
            const segmentId = feature.id;
            const baseStyle = `font-family:${fontFamily};font-size:14px;margin:4px 4px`;

            // Get the current impact area for the segment
            const currentSegment = tableData.find(s => s.segmentId === segmentId);
            const currentImpactArea = currentSegment?.impactArea || 'None';

            const notAddedHtml = `
                <p id="label-impact-${segmentId}" style="${baseStyle}">This road segment is:</p>
                <div role="radiogroup" aria-labelledby="label-impact-${segmentId}" style="display:flex;flex-direction:column;">
                    <label style="${baseStyle};cursor:pointer"><input type="radio" name="impact-area-${segmentId}" data-impact-select value="On Roadway" style="margin-right:6px"> On the project</label>
                    <label style="${baseStyle};cursor:pointer"><input type="radio" name="impact-area-${segmentId}" data-impact-select value="Parallel" style="margin-right:6px"> Parallel to the project</label>
                    <label style="${baseStyle};cursor:pointer"><input type="radio" name="impact-area-${segmentId}" data-impact-select value="Perpendicular" style="margin-right:6px"> Perpendicular to the project</label>
                    <label style="${baseStyle};cursor:pointer"><input type="radio" name="impact-area-${segmentId}" data-impact-select value="Before/After Roadway" style="margin-right:6px"> Before/After the project</label>
                </div>
                <p data-error aria-live="polite" style="${baseStyle};font-style:italic;color:${errorColor};display:block">
                    Make a selection to add road segment to the project
                </p>
                <div data-btn-wrapper style="display:none;justify-content:center;margin-top:8px">
                    <button type="button" data-add-btn aria-label="Add road segment to project" style="background:${primaryColor};color:${primaryContrast};
                        border:none;padding:5px 14px;cursor:pointer;font-family:${fontFamily};
                        border-radius:3px">
                        Add segment
                    </button>
                </div>`;

            const editHtml = `
                <p id="label-impact-edit-${segmentId}" style="${baseStyle}">Update impact area:</p>
                <div role="radiogroup" aria-labelledby="label-impact-edit-${segmentId}" style="display:flex;flex-direction:column;">
                    <label style="${baseStyle};cursor:pointer"><input type="radio" name="impact-area-edit-${segmentId}" data-impact-select-edit value="On Roadway" ${currentImpactArea === 'On Roadway' ? 'checked' : ''} style="margin-right:6px"> On the project</label>
                    <label style="${baseStyle};cursor:pointer"><input type="radio" name="impact-area-edit-${segmentId}" data-impact-select-edit value="Parallel" ${currentImpactArea === 'Parallel' ? 'checked' : ''} style="margin-right:6px"> Parallel to the project</label>
                    <label style="${baseStyle};cursor:pointer"><input type="radio" name="impact-area-edit-${segmentId}" data-impact-select-edit value="Perpendicular" ${currentImpactArea === 'Perpendicular' ? 'checked' : ''} style="margin-right:6px"> Perpendicular to the project</label>
                    <label style="${baseStyle};cursor:pointer"><input type="radio" name="impact-area-edit-${segmentId}" data-impact-select-edit value="Before/After Roadway" ${currentImpactArea === 'Before/After Roadway' ? 'checked' : ''} style="margin-right:6px"> Before/After the project</label>
                </div>
                <div data-edit-btn-wrapper style="display:flex;gap:8px;justify-content:center;margin-top:12px">
                    <button type="button" data-update-btn aria-label="Save road segment changes" style="background:${primaryColor};color:${primaryContrast};
                        border:none;padding:5px 14px;cursor:pointer;font-family:${fontFamily};
                        border-radius:3px">
                        Save segment
                    </button>
                    <button type="button" data-delete-edit-btn aria-label="Delete road segment from project" style="background:${errorColor};color:${primaryContrast};
                        border:none;padding:5px 14px;cursor:pointer;font-family:${fontFamily};
                        border-radius:3px">
                        Delete
                    </button>
                </div>`;

            const alreadyAddedHtml = `
                <p style="${baseStyle};font-style:italic;color:${successColor};padding-bottom:8px">
                    This road segment is already added.
                </p>
                <div style="display:flex;justify-content:center;margin-top:8px">
                    <button 
                        type="button" 
                        data-edit-btn 
                        aria-label="Edit road segment" 
                        style="background:${primaryColor};color:${primaryContrast};
                            border:none;padding:5px 14px;cursor:pointer;font-family:${fontFamily};
                            border-radius:3px">
                        Edit
                    </button>
                </div>`;

            layer.getPopup().setContent(`
            <div id="popup-container-${segmentId}" style="font-family:${fontFamily};outline:none" tabindex="-1">
                <h3 id="popup-title-${segmentId}" style="text-align:center;font-weight:bold;font-size:1rem;margin:0 0 8px;">
                    Road Segment Details<span style="${visuallyHidden}">.</span>
                </h3>
                <div id="popup-desc-${segmentId}">
                    <p style="${baseStyle}"><strong>Roadname:</strong> ${road}<span style="${visuallyHidden}">.</span></p>
                    <p style="${baseStyle}"><strong>From Road:</strong> ${from_road}<span style="${visuallyHidden}">.</span></p>
                    <p style="${baseStyle}"><strong>To Road:</strong> ${to_road}<span style="${visuallyHidden}">.</span></p>
                    <p style="${baseStyle}"><strong>Rank:</strong> ${arank}<span style="${visuallyHidden}">.</span></p>
                    ${isAdded && isEditing ? editHtml : (isAdded ? alreadyAddedHtml : notAddedHtml)}
                </div>                
            </div>`);

            const popupEl = layer.getPopup().getElement();
            if (popupEl) {
                const container = popupEl.querySelector(`#popup-container-${segmentId}`);
                requestAnimationFrame(() => {
                    container?.focus();
                });
            }

            // Thicken the line while popup is open for visibility (purely visual emphasis).
            // Highlighted+added segments are already thick/solid via geoJSONStyle, so this is a no-op for them.
            layer.setStyle({ weight: 10, color: layer.options.color, opacity: 1, dashArray: "" });

            if (isAdded && isEditing) {
                setTimeout(() => wireEditPopupEvents(layer, feature), 0);
            } else if (isAdded) {
                // Wire the edit button
                setTimeout(() => {
                    const editBtn = popupEl?.querySelector('[data-edit-btn]');
                    editBtn?.addEventListener('click', () => {
                        // Set ref directly so popupopen handler can access it immediately
                        editingSegmentIdRef.current = segmentId;
                        setEditingSegmentId(segmentId);
                        // Close and reopen popup to trigger popupopen event with new editingSegmentId
                        layer.closePopup();
                        setTimeout(() => {
                            layer.openPopup();
                        }, 50);
                    });
                }, 0);
            } else if (!isAdded) {
                setTimeout(() => wirePopupEvents(layer, feature), 0);
            }
        });

        // Handle popup close - revert to the segment's actual styled state (geoJSONStyle),
        // so highlighted+added segments correctly stay as a solid thick green line,
        // and everything else returns to its normal style.
        layer.on('popupclose', () => {
            const isAdded = addedSegmentIds.includes(feature.id);
            const isHighlighted = highlightedIds.includes(feature.id);

            if (isAdded && isHighlighted) {
                layer.setStyle({ weight: 10, color: SEGMENT_COLORS.added, opacity: 1, dashArray: "" });
            } else if (isAdded) {
                layer.setStyle({ weight: 5, color: SEGMENT_COLORS.added, dashArray: "10 6", fillOpacity: 0.7 });
            } else if (feature.properties.arank >= 1 && feature.properties.arank <= 100) {
                layer.setStyle({ weight: 5, color: SEGMENT_COLORS.top100, fillOpacity: 0.7 });
            } else {
                layer.setStyle({ weight: 5, color: SEGMENT_COLORS.texas100, dashArray: "1 12", lineCap: "round", fillOpacity: 0.7 });
            }
        });
    }

    //Adds a selected road segment from the popup.
    function handleAddSegment(feature, impactAreaVal, layer) {
        const isDuplicate = tableData.some((s) => s.segmentId === feature.id);
        if (!isDuplicate) {
            const newSegment = {
                segmentId: feature.id,
                roadName: feature.properties.road,
                fromRoad: feature.properties.from_road,
                toRoad: feature.properties.to_road,
                rank: feature.properties.arank,
                impactArea: impactAreaVal,
            };

            setTableData((prev) => {
                const sorted = [...prev, newSegment].sort((a, b) => {
                    if (a.impactArea === 'On Roadway' && b.impactArea !== 'On Roadway') return -1;
                    if (a.impactArea !== 'On Roadway' && b.impactArea === 'On Roadway') return 1;
                    return 0;
                });
                return sorted;
            });

            setAddedSegmentIds((prev) => [...prev, feature.id]);
            setRoadwayFields(newSegment, 'Add');
            layer.closePopup();
        }
    }

    //Updates a segment's impact area from the popup edit mode.
    function handleUpdateSegmentFromPopup(feature, newImpactArea, layer) {
        const segment = tableData.find(s => s.segmentId === feature.id);
        if (segment) {
            segment.impactArea = newImpactArea;
            setRoadwayFields(segment, "Update");

            setTableData((prevTableData) =>
                prevTableData.map((row) =>
                    row.segmentId === feature.id ? { ...row, impactArea: newImpactArea } : row
                )
            );

            editingSegmentIdRef.current = null;
            setEditingSegmentId(null);
            layer.closePopup();

            setAnnouncement(`Segment ${feature.properties.road} impact area updated to ${newImpactArea}.`);
        }
    }

    //Invoked to update formik object for conencted roadways.
    function setRoadwayFields(selectedSegment, taskName) {
        if (taskName === "Add") {

            // Create a new segment object
            const newSegment = {
                'segmentId': selectedSegment.segmentId,
                'roadName': selectedSegment.roadName,
                'fromRoad': selectedSegment.fromRoad,
                'toRoad': selectedSegment.toRoad,
                'rank': selectedSegment.rank,
                'impactArea': selectedSegment.impactArea
            };

            // Create a new array with the existing segments and the new segment
            let updatedConnectedRoadways = [...(formik.values.connectedRoadways || []), newSegment];

            // Update the connectedRoadways field in the formik form object
            formik.setFieldValue('connectedRoadways', updatedConnectedRoadways);
        }
        else if (taskName === "Delete") {
            let updatedRoadways = [];
            if (formik.values && formik.values.connectedRoadways) {
                formik.values.connectedRoadways.forEach((roadway) => {
                    if (roadway && roadway.segmentId !== selectedSegment.segmentId)
                        updatedRoadways.push(roadway)
                })
            }
            formik.setFieldValue('connectedRoadways', updatedRoadways);
        }
        else {
            let updatedRoadways = [];
            if (formik.values && formik.values.connectedRoadways) {
                formik.values.connectedRoadways.forEach((roadway) => {
                    if (roadway && roadway.segmentId === selectedSegment.segmentId)
                        roadway.impactArea = selectedSegment.impactArea
                    updatedRoadways.push(roadway)
                })
            }
            formik.setFieldValue('connectedRoadways', updatedRoadways);
        }
    }

    //Defines rules for different road segment for different scenarios.
    const geoJSONStyle = (feature) => {
        const isAdded = addedSegmentIds.includes(feature.id);
        const isHighlighted = highlightedIds.includes(feature.id);

        if (isAdded && isHighlighted) //Highlighted + added: solid thick green line.
            return { color: SEGMENT_COLORS.added, weight: 10, opacity: 1, dashArray: "" };
        else if (isAdded) //Set color to green (dashed) if road segment corresponds to an added segment.
            return { color: SEGMENT_COLORS.added, weight: 5, dashArray: "10 6" };
        else if (feature.properties.arank >= 1 && feature.properties.arank <= 100)   //Set color to yellow if it is a top 100 road.
            return { color: SEGMENT_COLORS.top100, weight: 5 };
        else    //Set default road segment color to blue.
            return { color: SEGMENT_COLORS.texas100, weight: 5, dashArray: "1 12", lineCap: "round" };
    }

    //Invoked when the "Impact Area" drop down is changed.
    function handleImpactAreaChange(selectedRow, newImpactArea) {

        //Update connected roadway details in form object.
        selectedRow.impactArea = newImpactArea;
        setRoadwayFields(selectedRow, "Update")

        // Update the impact area in the tableData state based on the selected value.
        setTableData((prevTableData) =>
            prevTableData.map((row) =>
                row.segmentId === selectedRow.segmentId ? { ...row, impactArea: newImpactArea } : row
            )
        );
    }

    //Road segment Deletion: Set action on clicking "Yes" for the selected road segment.
    const handleDeleteConfirm = () => {
        setConfirmationDialog((prevState) => ({ ...prevState, open: false }));
    };

    //Road segment Deletion: Set ConfirmationDialog properties for selected road segment.
    const handleDeleteSelected = (selectedIds, roadName = null) => {
        if (selectedIds && selectedIds.length > 0) {
            const messageText = roadName
                ? `Are you sure you want to delete "${roadName}"?`
                : `Are you sure you want to delete the selected road segment?`;

            setConfirmationDialog({
                open: true,
                title: "Delete Confirmation",
                message: messageText,
                onConfirm: () => {
                    const updatedTableData = tableData.filter((row) => !selectedIds.includes(row.segmentId));
                    const deletedRow = tableData.filter((row) => selectedIds.includes(row.segmentId));

                    //Set table data with updated data.
                    setTableData(updatedTableData);

                    //Add selected segment Id for geoJSONStyle.
                    setAddedSegmentIds(() => {
                        const updatedSegmentIds = updatedTableData.map(data => data.segmentId)
                        return updatedSegmentIds;
                    });

                    //Delete selected row from formik object.
                    setRoadwayFields(deletedRow[0], "Delete");

                    //Clear any deleted segments from the highlighted set.
                    setHighlightedIds((prev) => prev.filter((id) => !selectedIds.includes(id)));
                    setLastHighlightedId((prevLast) => (selectedIds.includes(prevLast) ? null : prevLast));

                    setSelectedRows([]);
                    handleDeleteConfirm();
                }
            },
            );
        }
    };

    //Toggles a single row's segment in/out of the highlighted set. Highlighting a row zooms/fits
    //the map to all currently highlighted segments and attempts to open the popup for the most
    //recently toggled-on segment. Announces the change for screen reader users.
    const handleToggleHighlight = (row) => {
        const segmentId = row.segmentId;
        setHighlightedIds((prev) => {
            const isCurrentlyHighlighted = prev.includes(segmentId);
            if (isCurrentlyHighlighted) {
                return prev.filter((id) => id !== segmentId);
            }
            return [...prev, segmentId];
        });
        const wasHighlighted = highlightedIds.includes(segmentId);
        setLastHighlightedId((prevLast) => (wasHighlighted ? prevLast : segmentId));
        setAnnouncement(
            wasHighlighted
                ? `Segment ${row.roadName} from ${row.fromRoad} to ${row.toRoad} unhighlighted.`
                : `Segment ${row.roadName} from ${row.fromRoad} to ${row.toRoad} highlighted.`
        );
    };

    //Header-level control: highlights every row in the table at once, or clears the highlight
    //set entirely if everything is already highlighted.
    const allSegmentIds = tableData.map((row) => row.segmentId);
    const allHighlighted = allSegmentIds.length > 0 && allSegmentIds.every((id) => highlightedIds.includes(id));

    const handleToggleHighlightAll = () => {
        if (allHighlighted) {
            setHighlightedIds([]);
            setLastHighlightedId(null);
            setAnnouncement(`All ${allSegmentIds.length} segments unhighlighted.`);
        } else {
            setHighlightedIds(allSegmentIds);
            setLastHighlightedId(allSegmentIds[allSegmentIds.length - 1] ?? null);
            setAnnouncement(`All ${allSegmentIds.length} segments highlighted.`);
        }
    };


    //Set columns.
    const columns = [
        { field: 'segmentId', headerName: 'Segment Id' },
        {
            field: 'roadName', headerName: 'Road Name', width: 250, renderCell: (params) => (
                <Typography variant="body2" noWrap>
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'fromRoad', headerName: 'From Road', width: 280, renderCell: (params) => (
                <Typography variant="body2" noWrap>
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'toRoad', headerName: 'To Road', width: 280, renderCell: (params) => (
                <Typography variant="body2" noWrap>
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'rank', headerName: 'Rank', width: 150, renderCell: (params) => (
                <Typography variant="body2">
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'impactArea',
            headerName: 'Impact Area',
            width: 230,
            renderCell: (params) => (
                <select
                    value={params.row.impactArea}
                    onChange={(e) => handleImpactAreaChange(params.row, e.target.value)}
                    aria-label={`Impact Area for ${params.row.roadName}`}
                >
                    <option value="None">Select impact area</option>
                    <option value="On Roadway">On Roadway</option>
                    <option value="Parallel">Parallel</option>
                    <option value="Perpendicular">Perpendicular</option>
                    <option value="Before/After Roadway">Before/After Roadway</option>
                </select>
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            type: 'actions',
            width: '160',
            renderHeader: () => (
                <Tooltip title={allHighlighted ? "Deselect all segments on map" : "Highlight all segments on map"}>
                    <span>
                        <IconButton
                            tabIndex={0}
                            size="small"
                            aria-label={allHighlighted ? "Deselect all segments on map" : "Highlight all segments on map"}
                            onClick={handleToggleHighlightAll}
                        >
                            {allHighlighted ? <DeselectIcon fontSize="small" /> : <SelectAllIcon fontSize="small" />}
                        </IconButton>
                        <Typography component="span" variant="body2" sx={{ ml: 0.5, fontWeight: 600 }}>
                            Actions
                        </Typography>
                    </span>
                </Tooltip>
            ),
            getActions: (params) => {
                const isHighlighted = highlightedIds.includes(params.row.segmentId);
                return [
                    <GridActionsCellItem
                        tabIndex={0}
                        key={`locate-${params.row.segmentId}`}
                        aria-label={isHighlighted ? "Remove segment highlight on map" : "Highlight segment on map"}
                        icon={isHighlighted ? <LocationOffIcon /> : <MyLocationIcon />}
                        label={isHighlighted ? "Unhighlight" : "Locate on map"}
                        onClick={() => handleToggleHighlight(params.row)}
                    />,
                    <GridActionsCellItem
                        tabIndex={0}
                        key={`delete-${params.row.segmentId}`}
                        aria-label="Delete Action"
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={() => handleDeleteSelected([params.row.segmentId])}
                    />
                ];
            }
        }
    ];

    const gridRef = useRef(null);

    const handleDeleteKeydown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            e.target.click();
        }
    }, []);

    useEffect(() => {
        if (!gridRef.current) return;

        const makeTabbable = () => {
            observer.disconnect();

            gridRef.current.querySelectorAll('.MuiDataGrid-columnHeader')
                .forEach((header) => header.setAttribute('tabindex', '0'));

            gridRef.current.querySelectorAll('.MuiDataGrid-iconButtonContainer button')
                .forEach((btn) => btn.setAttribute('tabindex', '0'));

            gridRef.current.querySelectorAll('.MuiDataGrid-cell button')
                .forEach((btn) => {
                    btn.setAttribute('tabindex', '0');
                    btn.removeEventListener('keydown', handleDeleteKeydown);
                    btn.addEventListener('keydown', handleDeleteKeydown);
                });

            observer.observe(gridRef.current, {
                subtree: true,
                attributes: true,
                attributeFilter: ['tabindex'],
                childList: true,
            });
        };

        const observer = new MutationObserver(makeTabbable);
        makeTabbable();

        return () => observer.disconnect();

    }, [tableData, handleDeleteKeydown]);


    return (
        <>
            {/* Visually hidden live region announcing segment highlight/unhighlight changes for screen reader users */}
            <div
                role="status"
                aria-live="polite"
                style={{
                    position: "absolute",
                    width: "1px",
                    height: "1px",
                    padding: 0,
                    margin: "-1px",
                    overflow: "hidden",
                    clip: "rect(0,0,0,0)",
                    border: 0,
                }}
            >
                {announcement}
            </div>

            {/* Map */}
            {!fetcher.isLoading && (
                <Alert role="status" severity="info" sx={{ marginBottom: "8px", marginRight: "10px" }}>
                    Click on any road segment on the map, or use the search dropdown below to connect to the project.
                </Alert>
            )}
            <Texas100RoadSegmentsMap
                data={connectedRoadwaysData}
                isLoading={fetcher.isLoading}
                onEachFeature={onEachRoad}
                styleFunction={geoJSONStyle}
                geoJSONKey={JSON.stringify({ connectedRoadwaysData, addedSegmentIds, highlightedIds })}
                mapStyle={{ marginRight: "10px", marginBottom: "8px" }}
                ariaLabel="Map showing Texas monitored road segments"
                highlightedIds={highlightedIds}
                lastHighlightedId={lastHighlightedId}
                layerRefs={layerRefs}
                onFeatureSelect={(id) => {
                    if (id == null) {
                        setHighlightedIds([]);
                        setLastHighlightedId(null);
                        setAnnouncement("Segment unhighlighted.");
                    } else {
                        setHighlightedIds([id]);
                        setLastHighlightedId(id);
                        const feature = connectedRoadwaysData.find((f) => f.id === id);
                        const label = feature
                            ? `${feature.properties.road} from ${feature.properties.from_road} to ${feature.properties.to_road}`
                            : "selected";
                        setAnnouncement(`Segment ${label} highlighted.`);
                    }
                }}
            />
            <Legend />

            {/* DataGrid containing Connected Roadways information */}
            {tableData != null && tableData.length > 0 && (
                <div
                    ref={gridRef}
                    aria-label="Connected Roadways Table"
                    style={{ marginRight: 10 }}
                >
                    <Typography aria-label="Connected Roadways Heading" sx={{ fontWeight: "bold", marginBottom: "10px", marginTop: "20px" }}>
                        Connected Roadways
                    </Typography>
                    <DataGrid
                        rows={tableData.slice().sort((a, b) => {
                            if (a.impactArea === "On Roadway" && b.impactArea !== "On Roadway") return -1;
                            else if (a.impactArea !== "On Roadway" && b.impactArea === "On Roadway") return 1;
                            else return 0;
                        })}
                        columns={columns}
                        getRowId={(row) => row.segmentId}
                        getRowClassName={(params) =>
                            highlightedIds.includes(params.row.segmentId) ? 'row-highlighted' : ''
                        }
                        sx={{
                            '& .row-highlighted': {
                                backgroundColor: 'success.light',
                                fontWeight: 700,
                                '&:hover': {
                                    backgroundColor: 'success.light',
                                },
                            },
                            '& .row-highlighted.Mui-selected': {
                                backgroundColor: 'success.light',
                            },
                            '& .row-highlighted .MuiDataGrid-cellContent': {
                                fontWeight: 700,
                            },
                            '& .row-highlighted .MuiTypography-root': {
                                fontWeight: 700,
                            },
                        }}
                        style={{ width: '100%' }}
                        autoHeight
                        initialState={{
                            columns: {
                                // Hide segmentId column from data grid view.
                                columnVisibilityModel: { 'segmentId': false },
                            },
                            pagination: {
                                paginationModel: { page: 0, pageSize: 5 },
                            },
                        }}
                        pageSizeOptions={[5, 10, 15, 20]}
                        onRowSelectionModelChange={(newRowSelectionModel) => {
                            setSelectedRows(newRowSelectionModel);
                        }}
                        selectedRows={selectedRows}
                        slotProps={{
                            pagination: {
                                labelRowsPerPage: 'Rows per page:',
                                SelectProps: {
                                    native: true,
                                    inputProps: {
                                        'aria-label': 'Rows per page',
                                        'id': 'pagination-select-input'
                                    }
                                }
                            },
                        }}
                    />
                </div>
            )}

            <div role="status" aria-live="polite">
                {!fetcher.isLoading && tableData.length === 0 && (
                    <Alert
                        tabIndex="0"
                        severity="error"
                        sx={{ marginBottom: '10px', marginRight: "10px" }}
                    >
                        No data to display. Select at least one road segment on map to proceed.
                    </Alert>
                )}
            </div>

            <ConfirmationDialog
                open={confirmationDialog.open}
                onClose={() => setConfirmationDialog((prevState) => ({ ...prevState, open: false }))}
                title={confirmationDialog.title}
                message={confirmationDialog.message}
                onConfirm={confirmationDialog.onConfirm}
            />
        </>
    );
}